import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Clipboard } from '@angular/cdk/clipboard';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { AppState } from '@zerops/zerops/app';
import { ZefSnackService } from '@zerops/zef/snack';
import { projectImport, projectExport } from '@zerops/zerops/core/project-base';
import { serviceStackImport, serviceStackExport } from '@zerops/zerops/core/service-stack-base';
import { RecipeBaseActions, recipeBaseState } from '@zerops/zerops/core/recipe-base';
import { select, Store } from '@ngrx/store';
import { Subject, Observable, combineLatest } from 'rxjs';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { selectZefProgressByType } from '@zerops/zef/progress';
import { FEATURE_NAME } from './import-export-dialog.constant';
import { ImportExportDialogForm } from './import-export-dialog.form';
import { DialogEntities, ImportExportDialogModes } from './import-export-dialog.model';
import { ThemeService } from '@zerops/zef/theme';
import { RECIPES, RecipeCategoryTypes, getRecipeByCategory, Recipe, RecipeEnvironment, resolveRecipeIconUrl, buildSlugIconUrlMap, RECIPE_ICON_POPULATE_PARAMS } from '@zerops/models/recipes';

export { DialogEntities } from './import-export-dialog.model';

@Component({
  selector: 'z-import-export-dialog',
  templateUrl: './import-export-dialog.container.html',
  styleUrls: [ './import-export-dialog.container.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportExportDialogContainer extends ZefReactiveComponent {

  // # Event Streams
  onClose$ = new Subject<void>();
  onProjectImport$ = new Subject<void>();
  onServiceStackImport$ = new Subject<void>();
  onSetEditorValue$ = new Subject<string>();
  onToggleReveal$ = new Subject<boolean>();

  // # Forms
  formState$ = this._importExportDialogForm.state$;

  // # Data
  // -- sync
  modes = ImportExportDialogModes;
  projectImportKey = projectImport.type;
  serviceStackImportKey = serviceStackImport.type;
  dialogEntities = DialogEntities;
  recipesLink = window.location.hostname + '/dashboard/recipes';
  recipeList = RECIPES.filter((d) => d.listed && !d.disabled);
  recipesByCategory = getRecipeByCategory(this.recipeList);
  recipeCategories = RecipeCategoryTypes;

  // New recipe system internal types (hardcoded for import dialog)
  importDialogTypes = [
    { type: 'example', name: 'Example Applications' },
    { type: 'project-utility', name: 'Open Source Apps and Software' }
  ];

  // -- async
  open$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map((data) => data.state)
  );
  yaml$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.yaml as string),
    tap(() => this._cdRef.markForCheck()),
    filter((d) => !!d)
  );
  mode$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.mode as ImportExportDialogModes),
    filter((d) => !!d)
  );
  entity$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.entity as DialogEntities),
    filter((d) => !!d)
  );
  projectId$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.projectId as string)
  );
  recipeSource$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.recipeSource as string)
  );
  recipeSourceUrl$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.recipeSourceUrl as string)
  );
  // id + current reveal state of the entity being exported, used to re-run the
  // export with decrypted secrets when the user flips the reveal toggle
  exportId$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => meta?.exportId as string)
  );
  reveal$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => !!meta?.reveal)
  );
  // Whether this viewer may export decrypted secrets. Resolved by whoever triggered
  // the export and carried in on the dialog meta - deliberately NOT derived here from
  // ProjectEntity/UserEntity: core dispatches into this feature, so importing core
  // back closes a cycle on the eager bootstrap path and the app fails to start.
  canRevealSecrets$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map(({ meta }) => !!meta?.canRevealSecrets)
  );
  // either export request in flight (covers the sudo re-auth round-trip too)
  exporting$ = combineLatest([
    this._store.pipe(select(selectZefProgressByType(projectExport.type))),
    this._store.pipe(select(selectZefProgressByType(serviceStackExport.type)))
  ]).pipe(
    map((progresses) => progresses.some((progress) => !!progress))
  );

  // # State resolver (will be initialized in constructor)
  state: any;

  // # Action Streams
  private _closeAction$ = this.onClose$.pipe(
    map(() => zefDialogClose({ key: FEATURE_NAME }))
  );
  private _setEditorValueAction$ = this.onSetEditorValue$.pipe(
    map((d) => this._importExportDialogForm.setValue('yaml', d))
  );
  private _projectImportAction$ = this.onProjectImport$.pipe(
    withLatestFrom(this.formState$, this.recipeSource$, this.recipeSourceUrl$),
    map(([ _, formState, recipeSource, recipeSourceUrl ]) => projectImport(
      {
        yaml: formState.controls.yaml.value,
        recipeSource,
        recipeSourceUrl
      },
      { type: 'pop' }
    ))
  );
  private _serviceStackImportAction$ = this.onServiceStackImport$.pipe(
    withLatestFrom(
      this.formState$,
      this.projectId$,
      this.recipeSource$,
      this.recipeSourceUrl$
    ),
    map(([ _, formState, projectId, recipeSource, recipeSourceUrl ]) => serviceStackImport(
      {
        data: {
          yaml: formState.controls.yaml.value,
          projectId,
          recipeSource,
          recipeSourceUrl
        }
      },
      { type: 'pop' }
    ))
  );

  // re-run the export with the chosen reveal state; if the user lacks sudo the
  // export fail carries a sudo-required error and the global sudo-reauth flow
  // re-dispatches this same action after re-authorization
  // `canRevealSecrets` is carried back through so the re-opened dialog keeps the
  // toggle - without it the export would return with the toggle gone and the user
  // could never switch it back off
  private _toggleRevealExportAction$ = this.onToggleReveal$.pipe(
    withLatestFrom(this.entity$, this.exportId$, this.canRevealSecrets$),
    filter(([ _, __, exportId ]) => !!exportId),
    map(([ reveal, entity, exportId, canRevealSecrets ]) => entity === DialogEntities.ServiceStack
      ? serviceStackExport({ id: exportId, reveal, canRevealSecrets })
      : projectExport({ id: exportId, reveal, canRevealSecrets })
    )
  );

  // Cache key for consistent caching
  private _generateCacheKey(baseKey: string, params: Record<string, any> = {}): string {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {} as Record<string, any>);

    const paramString = Object.keys(sortedParams).length > 0
      ? JSON.stringify(sortedParams)
      : '';

    return paramString ? `${baseKey}:${btoa(paramString)}` : baseKey;
  }

  // Parameters for recipe loading (load all recipes, filter on client side)
  private _recipeParams = {
    'filters[internalType][$in]': ['example', 'project-utility'],
    'pagination[pageSize]': 100,
    'populate[recipeCategories][populate]': '*',
    'populate[recipeLanguageFrameworks][populate]': '*',
    ...RECIPE_ICON_POPULATE_PARAMS
  };

  // Cache key for this specific request
  private _recipeCacheKey = this._generateCacheKey(FEATURE_NAME, this._recipeParams);

  // Simple recipe state streams (declared, initialized in constructor)
  newRecipes$: Observable<any>;
  recipeListRaw$: Observable<any>;
  isLoadingRecipes$: Observable<boolean>;

  // Signals (initialized in constructor after streams)
  recipeListRawSignal: any;
  isLoadingRecipesSignal: any;
  recipeLoadingState: any;
  isDarkMode = toSignal(this._themeService.isDarkMode$, { initialValue: false });
  newRecipesSignal: ReturnType<typeof toSignal<Recipe[]>>;
  recipeIconUrlMap: ReturnType<typeof computed<Record<string, string | undefined>>>;

  // New recipe loading action
  private _loadRecipesAction$ = this.open$.pipe(
    filter((open) => !!open),
    map(() => RecipeBaseActions.loadRecipes({
      key: FEATURE_NAME,
      params: this._recipeParams
    }))
  );

  constructor(
    private _cdRef: ChangeDetectorRef,
    private _store: Store<AppState>,
    private _clipboardService: Clipboard,
    private _snack: ZefSnackService,
    private _importExportDialogForm: ImportExportDialogForm,
    private _themeService: ThemeService
  ) {
    super();

    // Initialize streams after properties are set
    this.newRecipes$ = this._store.pipe(
      select(recipeBaseState.selectRecipeList(this._recipeCacheKey))
    );
    this.recipeListRaw$ = this._store.pipe(
      select(recipeBaseState.selectRecipeListRaw(this._recipeCacheKey))
    );
    this.isLoadingRecipes$ = this._store.pipe(
      select(selectZefProgressByType(RecipeBaseActions.loadRecipes.type)),
      map(progress => !!progress)
    );

    // Initialize signals after streams are set up
    this.recipeListRawSignal = toSignal(this.recipeListRaw$, { initialValue: undefined });
    this.isLoadingRecipesSignal = toSignal(this.isLoadingRecipes$, { initialValue: false });
    this.newRecipesSignal = toSignal(this.newRecipes$, { initialValue: [] as Recipe[] });
    this.recipeIconUrlMap = computed(() => {
      const isDark = this.isDarkMode();
      return buildSlugIconUrlMap(
        this.newRecipesSignal(),
        (recipe) => resolveRecipeIconUrl(recipe, isDark)
      );
    });

    // Computed loading states from signals - prioritize loading state first
    this.recipeLoadingState = computed(() => {
      const listRaw = this.recipeListRawSignal();
      const isLoading = this.isLoadingRecipesSignal();

      // If loading, determine what type of loading
      if (isLoading) {
        const isFirstLoad = listRaw === undefined;
        const result = {
          isLoading: true,
          isInitialLoading: isFirstLoad,
          isSubsequentLoading: !isFirstLoad,
          isEmpty: false,
          hasData: false,
          isFilteredEmpty: false
        };
        return result;
      }

      // Not loading - check data state
      const isFirstLoad = listRaw === undefined; // Never loaded
      const isEmpty = Array.isArray(listRaw) && listRaw.length === 0; // Loaded but empty
      const hasData = Array.isArray(listRaw) && listRaw.length > 0; // Has data

      const result = {
        isLoading: false,
        isInitialLoading: false,
        isSubsequentLoading: false,
        isEmpty,
        hasData,
        isFilteredEmpty: isEmpty && !isFirstLoad // Had data before, now empty due to filters
      };
      return result;
    });

    // Initialize state resolver
    this.state = this.$connect({
      open: this.open$,
      formState: this.formState$,
      mode: this.mode$,
      yaml: this.yaml$,
      entity: this.entity$,
      isDarkTheme: this._themeService.isDarkMode$,
      newRecipes: this.newRecipes$,
      isLoadingRecipes: this.isLoadingRecipes$,
      exportId: this.exportId$,
      reveal: this.reveal$,
      exporting: this.exporting$,
      canRevealSecrets: this.canRevealSecrets$
    });

    this.$dispatchActions([
      this._closeAction$,
      this._projectImportAction$,
      this._serviceStackImportAction$,
      this._setEditorValueAction$,
      this._loadRecipesAction$,
      this._toggleRevealExportAction$
    ]);
  }

  copy(yaml: string) {
    this._clipboardService.copy(yaml);
    this._snack.info$({ translation: 'general.copySuccess' });
  }

  // Get recipes for a specific internal type
  getRecipesForType(internalType: string): Recipe[] {
    const recipes = this.state.newRecipes || [];
    const result = recipes.filter((recipe: any) =>
      recipe.internalType === internalType
    );
    return result;
  }

  // Get all service utility recipes (for flattened service import view)
  getServiceUtilityRecipes(): Recipe[] {
    const recipes = this.state.newRecipes || [];
    const result = recipes.filter((recipe: any) =>
      recipe.internalType === 'project-utility'
    );
    return result;
  }

  // Get default environment for a recipe
  getDefaultEnvironment(recipe: Recipe): RecipeEnvironment | null {
    if (!recipe.sourceData?.environments?.length) return null;

    // Priority: production > staging > development > first available
    const envPriority = ['production', 'staging', 'development'];

    for (const priority of envPriority) {
      const env = recipe.sourceData.environments.find(e =>
        e.name.toLowerCase().includes(priority)
      );
      if (env) return env;
    }

    return recipe.sourceData.environments[0];
  }

  // Handle recipe selection (single environment or default)
  onSelectRecipe(recipe: Recipe) {
    const defaultEnv = this.getDefaultEnvironment(recipe);
    if (defaultEnv) {
      this.onSetEditorValue$.next(defaultEnv.import);
    }
  }

  // Handle recipe selection with specific environment
  onSelectRecipeEnvironment(environment: RecipeEnvironment) {
    this.onSetEditorValue$.next(environment.import);
  }

}
